import React from 'react';
import { Link } from 'react-router-dom';
import { about, experience, projects, blogs, commercialProjects } from '../../portfolio';
import { Briefcase, ArrowRight, ExternalLink } from 'lucide-react';
import TwitterIcon from '@material-ui/icons/Twitter';
import GitHubIcon from '@material-ui/icons/GitHub';
import LinkedInIcon from '@material-ui/icons/LinkedIn';

const Home = () => {
  const firstName = about.name.split(' ')[0];

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="hero-content-wrapper">
          <div className="hero-text">
            <h1>Hi, I'm {firstName}!</h1>
            <h2>{about.role}</h2>
            <p className="hero-pron">{about.description}</p>
            
            <p className="currently-label">Currently</p>
            <div className="currently-list">
              {experience.length > 0 && (
                <div className="currently-item">
                  <span className="tag-dot" style={{ backgroundColor: '#0ea5e9', marginTop: '6px' }} />
                  <span>Working as <strong>{experience[0].role}</strong> for <a href="#">{experience[0].company}</a>.</span>
                </div>
              )}
              {projects.length > 0 && (
                <div className="currently-item">
                  <span className="tag-dot" style={{ backgroundColor: '#a855f7', marginTop: '6px' }} />
                  <span>Building <a href={projects[0].livePreview || projects[0].sourceCode} target="_blank" rel="noopener noreferrer">{projects[0].name}</a>.</span>
                </div>
              )}
              <div className="currently-item">
                <span className="tag-dot" style={{ backgroundColor: '#10b981', marginTop: '6px' }} />
                <span>Freelancing and helping clients worldwide.</span>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="/roshandb.png" 
              alt={about.name} 
              onError={(e) => e.target.src='/images/roshandb.jpg'} 
            />
          </div>
        </div>
      </section>

      {/* PERSONAL PROJECTS */}
      <section className="section">
        <div className="section-header">
          <h1 className="section-title">Personal Projects</h1>
          <p className="section-subtitle">Tinkering through code in various domains</p>
        </div>

        <div className="project-grid">
          {projects.map((proj, idx) => (
            <a 
              key={idx} 
              href={proj.livePreview || proj.sourceCode || '#'} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="project-card"
            >
              <ExternalLink className="project-link-icon" />
              <div className="project-logo" style={{ height: '48px', width: 'auto', maxWidth: '140px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', backgroundColor: 'transparent', border: 'none' }}>
                <img 
                  src={
                    proj.name.includes('Tourmoo') ? '/images/trustedby/tourmoologo (1).png' : 
                    proj.name.includes('Locomoo') ? '/images/trustedby/locomoo.png' : 
                    proj.name.includes('PicBee')  ? '/images/trustedby/picbee.png' : 
                    `/${proj.name.split(' - ')[0].toLowerCase().replace(/\s+/g, '')}.png`
                  } 
                  alt={proj.name} 
                  style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'left center' }} 
                  onError={(e) => { 
                    e.target.style.display = 'none'; 
                    e.target.nextSibling.style.display = 'block'; 
                  }} 
                />
                <span style={{ display: 'none', fontWeight: 'bold' }}>{proj.name.substring(0, 3).toUpperCase()}</span>
              </div>
              
              <div className="project-info">
                <div className="project-name">
                  {proj.name}
                  {idx < 2 && <span className="tag-dot"></span>}
                </div>
                <div className="project-desc">{proj.description}</div>
              </div>
              {proj.stack && (
                <div className="project-tags">
                  {proj.stack.map(tag => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
              )}
            </a>
          ))}
        </div>
      </section>

      {/* TRUSTED BY */}
      <section className="section" style={{ borderTop: '1px solid var(--border)', paddingTop: '64px', marginTop: '64px' }}>
        <h3 className="trusted-label">Trusted by Companies like</h3>
        <div className="trusted-logos">
          <div className="marquee-track">
            {[
              { name: 'Tourmoo', src: '/images/trustedby/tourmoologo (1).png', href: 'https://tourmoo.com' },
              { name: 'Locomoo', src: '/images/trustedby/locomoo.png', href: 'https://locomoo.app' },
              { name: 'PicBee', src: '/images/trustedby/picbee.png', href: '#' },
              { name: 'Rackly', src: '/images/trustedby/rackly.png', href: '#' },
              { name: 'Lenden', src: '/images/trustedby/lenden.png', href: '#' },
              { name: 'Domainly', src: '/images/trustedby/domainly.png', href: '#' }
            ].map(partner => (
               <a key={partner.name} href={partner.href} target="_blank" rel="noopener noreferrer" className="trusted-logo-link">
                 <img src={partner.src} alt={partner.name} title={partner.name} />
               </a>
            ))}
            {[
              { name: 'Tourmoo2', src: '/images/trustedby/tourmoologo (1).png', href: 'https://tourmoo.com' },
              { name: 'Locomoo2', src: '/images/trustedby/locomoo.png', href: 'https://locomoo.app' },
              { name: 'PicBee2', src: '/images/trustedby/picbee.png', href: '#' },
              { name: 'Rackly2', src: '/images/trustedby/rackly.png', href: '#' },
              { name: 'Lenden2', src: '/images/trustedby/lenden.png', href: '#' },
              { name: 'Domainly2', src: '/images/trustedby/domainly.png', href: '#' }
            ].map(partner => (
               <a key={partner.name} href={partner.href} target="_blank" rel="noopener noreferrer" className="trusted-logo-link">
                 <img src={partner.src} alt={partner.name} title={partner.name} />
               </a>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO (EXPERIENCE) */}
      <section className="section">
        <div className="section-header">
          <h1 className="section-title">Portfolio</h1>
          <p className="section-subtitle">Work I have done for my clients</p>
        </div>

        <div className="portfolio-grid">
          {[...projects.filter(p => ['Tourmoo', 'Locomoo', 'PicBee'].includes(p.name.split(' - ')[0])), ...commercialProjects].map((proj, idx) => {
            const imgName = proj.imageFallback || proj.name.split(' - ')[0].toLowerCase().replace(/\s+/g, '');
            let srcPath = `/${imgName}.jpg`;
            if (proj.name.includes('Tourmoo')) srcPath = '/images/trustedby/tourmoologo (1).png';
            if (proj.name.includes('Locomoo')) srcPath = '/images/trustedby/locomoo.png';
            if (proj.name.includes('PicBee')) srcPath = '/images/trustedby/picbee.png';
            
            return (
              <div key={idx} className="portfolio-card" style={{ cursor: 'pointer' }} onClick={() => window.open(proj.livePreview, '_blank')}>
                <div className="portfolio-thumb" style={['Tourmoo', 'Locomoo', 'PicBee'].includes(proj.name.split(' - ')[0]) ? {backgroundColor: 'var(--surface)'} : {}}>
                  <img 
                    src={srcPath} 
                    alt={proj.name} 
                    style={['Tourmoo', 'Locomoo', 'PicBee'].includes(proj.name.split(' - ')[0]) ? {objectFit: 'contain', padding: '32px', boxSizing: 'border-box'} : {}}
                    onError={(e) => { 
                      if (!srcPath.includes('/images/trustedby/')) {
                        if (!e.target.dataset.fallback) {
                          e.target.dataset.fallback = 'true';
                          e.target.src = `/${imgName}.jpeg`; 
                        } else if (!e.target.dataset.fallbackPng) {
                          e.target.dataset.fallbackPng = 'true';
                          e.target.src = `/${imgName}.png`;
                        } else {
                          e.target.style.display = 'none';
                        }
                      } else {
                        e.target.style.display = 'none';
                      }
                    }} 
                  />
                </div>
                <div className="portfolio-header">
                  <div className="portfolio-name">{proj.name.split(' - ')[0]}</div>
                  <div className="portfolio-meta" style={{ fontSize: '12px', color: 'var(--text-3)' }}>{proj.stack?.slice(0, 2).join(', ')}</div>
                </div>
                <div className="portfolio-desc">
                  {proj.description.length > 100 ? proj.description.substring(0, 100) + '...' : proj.description}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* WRITING */}
      <section className="section">
        <div className="section-header">
          <h1 className="section-title">Writing</h1>
          <p className="section-subtitle">Things I work on and learn about</p>
        </div>

        <div className="writing-list">
          {blogs.slice(0, 4).map((blog, idx) => (
            <Link key={idx} to={`/blogs/${blog.slug}`} className="writing-item">
              <span className="writing-title">{blog.title}</span>
              <span className="writing-date">
                {new Date(blog.publishedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </span>
            </Link>
          ))}
        </div>

        <Link to="/blogs" className="btn" style={{marginTop: '24px'}}>
          See all posts <ArrowRight size={14} style={{marginLeft: '8px'}} />
        </Link>
      </section>

      {/* PHOTOS */}
      <section className="section">
        <div className="section-header">
          <h1 className="section-title">Photos</h1>
          <p className="section-subtitle">Pictures from my travels</p>
        </div>
        <div className="photo-grid">
          <div className="photo-item"><img src="/images/roshanimage1.jpg" alt="Roshan 1" /></div>
          <div className="photo-item"><img src="/images/roshanimage2.jpg" alt="Roshan 2" /></div>
          <div className="photo-item"><img src="/images/roshanimage3.jpg" alt="Roshan 3" /></div>
          <div className="photo-item"><img src="/images/roshanimage4.jpg" alt="Roshan 4" /></div>
          <div className="photo-item"><img src="/images/roshanimage5.jpg" alt="Roshan 5" /></div>
        </div>
      </section>
      
      <footer className="footer">
        <div className="footer-socials">
          {about.social.tweet && <a href={about.social.tweet}><TwitterIcon style={{ fontSize: 20 }} /></a>}
          {about.social.github && <a href={about.social.github}><GitHubIcon style={{ fontSize: 20 }} /></a>}
          {about.social.linkedin && <a href={about.social.linkedin}><LinkedInIcon style={{ fontSize: 20 }} /></a>}
        </div>
        <div>End of line.</div>
      </footer>
    </>
  );
};

export default Home;
